import { useEffect, useMemo, useState } from "react";
import { listDailyBalances, type DailyBalance } from "@/lib/api";
import { formatAmount } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, Badge, type ColumnDef } from "@/components/DataTable";
import { CreditCard, Smartphone, Info, Layers } from "lucide-react";

// Type utilisé quand on affiche l'agrégat "Tous wallets" (MM + Cards fusionnés
// par (date, currency)). Le back renvoie des lignes par wallet, on agrège côté
// client parce que c'est bête et efficace pour 2 wallets × 30 jours.
type AggregatedRow = {
  balance_date: string;
  currency: string;
  wallet_types: Array<"mobile_money" | "cards">;
  opening_balance_minor: number;
  closing_balance_minor: number | null; // null si au moins un des sous-wallets n'est pas encore clôturé
  credits_minor: number;
  debits_minor: number;
  w2b_minor: number;
  movements_count: number;
};

function aggregateByDayCurrency(rows: DailyBalance[]): AggregatedRow[] {
  const map = new Map<string, AggregatedRow>();
  for (const r of rows) {
    const key = `${r.balance_date}|${r.currency}`;
    const existing = map.get(key);
    if (existing) {
      existing.wallet_types.push(r.wallet_type);
      existing.opening_balance_minor += r.opening_balance_minor;
      existing.credits_minor += r.credits_minor;
      existing.debits_minor += r.debits_minor;
      existing.w2b_minor += r.w2b_minor;
      existing.movements_count += r.movements_count;
      // Closing agrégé = null si au moins un wallet pas encore clôturé
      if (r.closing_balance_minor === null || existing.closing_balance_minor === null) {
        existing.closing_balance_minor = null;
      } else {
        existing.closing_balance_minor += r.closing_balance_minor;
      }
    } else {
      map.set(key, {
        balance_date: r.balance_date,
        currency: r.currency,
        wallet_types: [r.wallet_type],
        opening_balance_minor: r.opening_balance_minor,
        closing_balance_minor: r.closing_balance_minor,
        credits_minor: r.credits_minor,
        debits_minor: r.debits_minor,
        w2b_minor: r.w2b_minor,
        movements_count: r.movements_count,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.balance_date.localeCompare(a.balance_date));
}

// Une ligne = 1 wallet × 1 jour. Alimenté par le cron A côté serveur
// (`/opt/moko-aviation/scripts/daily_balances.sh open|close`) — cf ADR/migration
// 016. Solde d'ouverture posé à 00:05, clôture à 23:55.
//
// Équation invariant (pour un jour clôturé) :
//   opening + credits − debits = closing
//   (dont debits inclut refunds + w2b + commissions)
//
// Utilité finance : voir facilement combien il y avait au wallet le matin,
// combien est entré, combien est sorti (dont W2B vers TMB), et combien il reste
// le soir. Résout la douleur "j'ai encaissé 462 mais il reste 134 dans le wallet
// carte" — la réponse est dans l'opening du matin (accumulé J-1).

const DEFAULT_DAYS = 14;

function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

export function DailyBalancesPage() {
  const [rows, setRows] = useState<DailyBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(isoDaysAgo(DEFAULT_DAYS));
  const [dateTo, setDateTo] = useState(isoToday());
  const [walletFilter, setWalletFilter] = useState<"" | "mobile_money" | "cards">("");
  const [currencyFilter, setCurrencyFilter] = useState<"" | "USD" | "CDF">("");

  useEffect(() => {
    setLoading(true);
    listDailyBalances({
      date_from: dateFrom,
      date_to: dateTo,
      wallet_type: walletFilter || undefined,
      currency: currencyFilter || undefined,
      limit: 500,
    })
      .then((r) => setRows(r.data))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, walletFilter, currencyFilter]);

  // Vue agrégée (par jour, par devise) quand aucun wallet n'est sélectionné.
  const isAggregated = walletFilter === "";
  const aggregatedRows = useMemo(
    () => (isAggregated ? aggregateByDayCurrency(rows) : []),
    [isAggregated, rows],
  );

  const aggregatedColumns: ColumnDef<AggregatedRow>[] = useMemo(
    () => [
      {
        key: "date",
        header: "Date",
        minWidth: 110,
        sortable: true,
        accessor: (r) => r.balance_date,
        cell: (r) => <span className="font-semibold text-[#181c32]">{r.balance_date}</span>,
      },
      {
        key: "wallets",
        header: "Wallets",
        minWidth: 180,
        cell: (r) => (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-brand-50 text-brand-700">
              <Layers size={14} />
            </div>
            <div className="text-xs">
              <div className="font-semibold text-[#181c32]">
                Tous ({r.wallet_types.length})
              </div>
              <div className="text-[#7e8299]">
                {r.wallet_types
                  .map((t) => (t === "mobile_money" ? "MM" : "Cartes"))
                  .join(" + ")}{" "}
                · {r.currency}
              </div>
            </div>
          </div>
        ),
      },
      {
        key: "opening",
        header: "Solde ouverture",
        align: "right",
        minWidth: 130,
        sortable: true,
        accessor: (r) => r.opening_balance_minor,
        cell: (r) => (
          <span className="font-semibold text-[#181c32]">
            {formatAmount(r.opening_balance_minor, r.currency)}
          </span>
        ),
      },
      {
        key: "credits",
        header: "Crédits",
        align: "right",
        minWidth: 120,
        sortable: true,
        accessor: (r) => r.credits_minor,
        cell: (r) => (
          <span className={r.credits_minor > 0 ? "text-success-700 font-semibold" : "text-[#7e8299]"}>
            +{formatAmount(r.credits_minor, r.currency)}
          </span>
        ),
      },
      {
        key: "debits",
        header: "Débits",
        align: "right",
        minWidth: 120,
        sortable: true,
        accessor: (r) => r.debits_minor,
        cell: (r) => (
          <span className={r.debits_minor > 0 ? "text-danger-700 font-semibold" : "text-[#7e8299]"}>
            −{formatAmount(r.debits_minor, r.currency)}
          </span>
        ),
      },
      {
        key: "w2b",
        header: "W2B (dont)",
        align: "right",
        minWidth: 120,
        cell: (r) => (
          <span className={r.w2b_minor > 0 ? "text-[#3f4254]" : "text-[#c8c8d0]"}>
            {r.w2b_minor > 0 ? `−${formatAmount(r.w2b_minor, r.currency)}` : "—"}
          </span>
        ),
      },
      {
        key: "closing",
        header: "Solde clôture",
        align: "right",
        minWidth: 130,
        sortable: true,
        accessor: (r) => r.closing_balance_minor ?? -1,
        cell: (r) =>
          r.closing_balance_minor === null ? (
            <Badge color="warning">En cours</Badge>
          ) : (
            <span className="font-bold text-[#181c32]">
              {formatAmount(r.closing_balance_minor, r.currency)}
            </span>
          ),
      },
      {
        key: "count",
        header: "Mvts",
        align: "right",
        minWidth: 60,
        sortable: true,
        accessor: (r) => r.movements_count,
        cell: (r) => <span className="text-xs text-[#7e8299]">{r.movements_count}</span>,
      },
    ],
    [],
  );

  const columns: ColumnDef<DailyBalance>[] = useMemo(
    () => [
      {
        key: "date",
        header: "Date",
        minWidth: 110,
        sortable: true,
        accessor: (r) => r.balance_date,
        cell: (r) => <span className="font-semibold text-[#181c32]">{r.balance_date}</span>,
      },
      {
        key: "wallet",
        header: "Wallet",
        minWidth: 150,
        cell: (r) => (
          <div className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                r.wallet_type === "mobile_money"
                  ? "bg-success-50 text-success-700"
                  : "bg-info-50 text-info-700"
              }`}
            >
              {r.wallet_type === "mobile_money" ? (
                <Smartphone size={14} />
              ) : (
                <CreditCard size={14} />
              )}
            </div>
            <div className="text-xs">
              <div className="font-semibold text-[#181c32]">
                {r.wallet_type === "mobile_money" ? "Mobile Money" : "Cartes"}
              </div>
              <div className="text-[#7e8299]">{r.currency}</div>
            </div>
          </div>
        ),
      },
      {
        key: "opening",
        header: "Solde ouverture",
        align: "right",
        minWidth: 130,
        sortable: true,
        accessor: (r) => r.opening_balance_minor,
        cell: (r) => (
          <span className="font-semibold text-[#181c32]">
            {formatAmount(r.opening_balance_minor, r.currency)}
          </span>
        ),
      },
      {
        key: "credits",
        header: "Crédits",
        align: "right",
        minWidth: 120,
        sortable: true,
        accessor: (r) => r.credits_minor,
        cell: (r) => (
          <span className={r.credits_minor > 0 ? "text-success-700 font-semibold" : "text-[#7e8299]"}>
            +{formatAmount(r.credits_minor, r.currency)}
          </span>
        ),
      },
      {
        key: "debits",
        header: "Débits",
        align: "right",
        minWidth: 120,
        sortable: true,
        accessor: (r) => r.debits_minor,
        cell: (r) => (
          <span className={r.debits_minor > 0 ? "text-danger-700 font-semibold" : "text-[#7e8299]"}>
            −{formatAmount(r.debits_minor, r.currency)}
          </span>
        ),
      },
      {
        key: "w2b",
        header: "W2B (dont)",
        align: "right",
        minWidth: 120,
        cell: (r) => (
          <span className={r.w2b_minor > 0 ? "text-[#3f4254]" : "text-[#c8c8d0]"}>
            {r.w2b_minor > 0 ? `−${formatAmount(r.w2b_minor, r.currency)}` : "—"}
          </span>
        ),
      },
      {
        key: "closing",
        header: "Solde clôture",
        align: "right",
        minWidth: 130,
        sortable: true,
        accessor: (r) => r.closing_balance_minor ?? -1,
        cell: (r) =>
          r.closing_balance_minor === null ? (
            <Badge color="warning">En cours</Badge>
          ) : (
            <span className="font-bold text-[#181c32]">
              {formatAmount(r.closing_balance_minor, r.currency)}
            </span>
          ),
      },
      {
        key: "count",
        header: "Mvts",
        align: "right",
        minWidth: 60,
        sortable: true,
        accessor: (r) => r.movements_count,
        cell: (r) => <span className="text-xs text-[#7e8299]">{r.movements_count}</span>,
      },
    ],
    [],
  );

  return (
    <div>
      <PageHeader
        title="Soldes quotidiens"
        breadcrumbs={[{ label: "Finance" }, { label: "Soldes quotidiens" }]}
      />

      <div className="rounded-xl bg-brand-50 border border-brand-100 p-4 mb-4 flex gap-3">
        <Info size={18} className="text-brand-700 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-[#3f4254] leading-relaxed">
          <div className="font-semibold text-brand-800 mb-1">Comment lire cette page ?</div>
          Chaque ligne = <span className="font-semibold">1 wallet</span> ×{" "}
          <span className="font-semibold">1 journée</span>. L'invariant :{" "}
          <span className="font-mono">Ouverture + Crédits − Débits = Clôture</span>. Le solde
          d'ouverture reprend la clôture de la veille (accumulé non-viré), la clôture est le solde
          réel du wallet à 23:59.
        </div>
      </div>

      {isAggregated ? (
        <DataTable<AggregatedRow>
          title="Historique des soldes (agrégé par jour)"
          subtitle="Somme MM + Cartes par (date, devise). Filtre un wallet pour la vue détaillée."
          columns={aggregatedColumns}
          data={aggregatedRows}
          rowKey={(r) => `${r.balance_date}|${r.currency}`}
          loading={loading}
          initialSort={{ key: "date", dir: "desc" }}
          filtersSlot={
            <>
              <label className="text-xs text-[#7e8299] flex items-center gap-2">
                Du
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="form-input py-1.5 px-2 text-sm"
                />
              </label>
              <label className="text-xs text-[#7e8299] flex items-center gap-2">
                Au
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="form-input py-1.5 px-2 text-sm"
                />
              </label>
              <select
                value={walletFilter}
                onChange={(e) => setWalletFilter(e.target.value as typeof walletFilter)}
                className="form-input py-2 px-3 text-sm w-auto"
              >
                <option value="">Tous wallets (agrégé)</option>
                <option value="mobile_money">Mobile Money</option>
                <option value="cards">Cartes</option>
              </select>
              <select
                value={currencyFilter}
                onChange={(e) => setCurrencyFilter(e.target.value as typeof currencyFilter)}
                className="form-input py-2 px-3 text-sm w-auto"
              >
                <option value="">Toutes devises</option>
                <option value="USD">USD</option>
                <option value="CDF">CDF</option>
              </select>
            </>
          }
          emptyMessage="Aucun solde quotidien pour cette période"
        />
      ) : (
      <DataTable<DailyBalance>
        title="Historique des soldes"
        columns={columns}
        data={rows}
        rowKey={(r) => `${r.wallet_id}|${r.balance_date}`}
        loading={loading}
        initialSort={{ key: "date", dir: "desc" }}
        filtersSlot={
          <>
            <label className="text-xs text-[#7e8299] flex items-center gap-2">
              Du
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="form-input py-1.5 px-2 text-sm"
              />
            </label>
            <label className="text-xs text-[#7e8299] flex items-center gap-2">
              Au
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="form-input py-1.5 px-2 text-sm"
              />
            </label>
            <select
              value={walletFilter}
              onChange={(e) => setWalletFilter(e.target.value as typeof walletFilter)}
              className="form-input py-2 px-3 text-sm w-auto"
            >
              <option value="">Tous wallets</option>
              <option value="mobile_money">Mobile Money</option>
              <option value="cards">Cartes</option>
            </select>
            <select
              value={currencyFilter}
              onChange={(e) => setCurrencyFilter(e.target.value as typeof currencyFilter)}
              className="form-input py-2 px-3 text-sm w-auto"
            >
              <option value="">Toutes devises</option>
              <option value="USD">USD</option>
              <option value="CDF">CDF</option>
            </select>
          </>
        }
        emptyMessage="Aucun solde quotidien pour cette période"
      />
      )}
    </div>
  );
}
